import React, { useEffect, useState } from "react";
import { Box, Grid } from "@mui/material";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import Loader from "../../components/Molecules/Loader/Loader";
import { getResearchQueryRunScoringConfigById } from "../../services/scoringService";
import { getActiveRunInstance } from "../../services/runInstance";
import { getResearchQueriesList } from "../../services/ResearchQueriesService";
import { useBreadCrumbContext } from "../../contexts/BreadCrumbContext";
import { useAuth } from "../../contexts/AuthContext";
import { IconBtnEditBase } from "../../common/utils/icons";

const ViewResearchQueryRunScoring: React.FC = () => {
  const { id: configId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canEdit = hasPermission("research_query_run_scoring_config", "edit");
  const { setCurrentBreadcrumbPage } = useBreadCrumbContext();
  const [isLoading, setIsLoading] = useState(false);
  const [configData, setConfigData] = useState<any>(
    location.state?.rowData || null,
  );
  const [runMap, setRunMap] = useState<Map<number, string>>(new Map());
  const [grqMap, setGrqMap] = useState<Map<number, string>>(new Map());

  // Breadcrumb
  useEffect(() => {
    setCurrentBreadcrumbPage({
      name: `Config #${configId}`,
      id: configId ? parseInt(configId) : null,
    });
    return () => setCurrentBreadcrumbPage({ name: "", id: null });
  }, [configId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Build lookup maps on mount ─────────────────────────────────────────────
  useEffect(() => {
    getActiveRunInstance()
      .then((runs) => {
        if (Array.isArray(runs)) {
          const m = new Map<number, string>();
          runs.forEach((r: any) => {
            if (r.id != null) m.set(r.id, r.run_name);
          });
          setRunMap(m);
        }
      })
      .catch(() => {});

    getResearchQueriesList({ page: 1, pSize: 10000, isActive: "all" })
      .then((result) => {
        const items = result?.items || [];
        if (Array.isArray(items)) {
          const m = new Map<number, string>();
          items.forEach((q: any) => {
            if (q.id != null) m.set(q.id, q.name);
          });
          setGrqMap(m);
        }
      })
      .catch(() => {});
  }, []);

  // ── Always fetch fresh data from API (handles direct URL access) ──────────
  useEffect(() => {
    if (!configId) return;
    const numericId = Number(configId);
    setIsLoading(true);
    getResearchQueryRunScoringConfigById(numericId)
      .then((d) => {
        if (!d) return;
        if (Array.isArray(d)) {
          const found = d.find((item: any) => item.id === numericId) || d[0];
          if (found) setConfigData(found);
        } else {
          setConfigData(d);
        }
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, [configId]); // eslint-disable-line react-hooks/exhaustive-deps

  const field = (label: string, value: any, md: number = 4) => (
    <Grid item xs={12} sm={6} md={md}>
      <label className="label-text">{label}</label>
      <div className="form-control">{value ?? "—"}</div>
    </Grid>
  );

  const runName = configData?.run_instance_id
    ? (runMap.get(configData.run_instance_id) ??
      `Run #${configData.run_instance_id}`)
    : null;

  const grqName = configData?.generic_research_query_id
    ? (grqMap.get(configData.generic_research_query_id) ??
      `Research Query #${configData.generic_research_query_id}`)
    : null;

  return (
    <Box>
      {isLoading && <Loader isLoading={isLoading} />}
      <Box className="text-box-card-white">
        <Box className="text-box-header">
          <h3>View Research Query Run Scoring Config</h3>
        </Box>
        <Grid
          container
          rowSpacing={2.5}
          columnSpacing={{ xl: 4, lg: 3, md: 2, sm: 2, xs: 1 }}
        >
          {field("Run", runName)}
          {field("Research Query", grqName)}
          {canEdit && (
            <Grid
              item
              xs={12}
              sm={6}
              md
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
              }}
            >
              <button
                className="btn-nostyle icon-btn-edit"
                onClick={() =>
                  navigate(`/scoring/research-query-run/edit/${configId}`)
                }
              >
                <Box className="d-block">
                  <IconBtnEditBase ariaLabel="Edit Research Query Run Scoring Config" />
                </Box>
              </button>
            </Grid>
          )}
        </Grid>
        <Grid
          container
          rowSpacing={2.5}
          columnSpacing={{ xl: 4, lg: 3, md: 2, sm: 2, xs: 1 }}
          sx={{ mt: "20px" }}
        >
          {field("Severity", configData?.severity)}
          {field("Factor", configData?.factor)}
        </Grid>
      </Box>
    </Box>
  );
};

export default ViewResearchQueryRunScoring;
